import { cn } from "@/lib/utils";
import { Radio } from "lucide-react";
import { useState } from "react";

interface CompositeFaviconProps {
  faviconUrls: string[] | null | undefined;
  size?: "sm" | "md" | "lg";
  className?: string;
  fallbackIcon?: React.ReactNode;
}

const sizeConfig = {
  sm: { containerPx: 24, iconClass: "w-4 h-4" },
  md: { containerPx: 32, iconClass: "w-5 h-5" },
  lg: { containerPx: 48, iconClass: "w-8 h-8" },
};

export function CompositeFavicon({ 
  faviconUrls, 
  size = "md", 
  className,
  fallbackIcon
}: CompositeFaviconProps) {
  const config = sizeConfig[size];
  const containerPx = config.containerPx;
  const validUrls = faviconUrls?.filter(url => url) || [];
  const [failedUrls, setFailedUrls] = useState<Set<string>>(new Set());
  
  const handleImageError = (url: string) => {
    setFailedUrls(prev => new Set(prev).add(url));
  };
  
  const workingUrls = validUrls.filter(url => !failedUrls.has(url));
  
  if (workingUrls.length === 0) {
    return fallbackIcon || (
      <div 
        className={cn("flex items-center justify-center", className)}
        style={{ width: containerPx, height: containerPx }}
      >
        <Radio className={cn(config.iconClass, "text-primary-600")} />
      </div>
    );
  }

  if (workingUrls.length === 1) {
    return (
      <img 
        src={workingUrls[0]} 
        alt="Station logo" 
        className={cn("rounded-lg object-cover", className)}
        style={{ width: containerPx, height: containerPx }}
        onError={() => handleImageError(workingUrls[0])}
      />
    );
  }

  const displayUrls = workingUrls.slice(0, 3);
  const count = displayUrls.length;
  
  const iconSizePx = count === 2 
    ? Math.round(containerPx * 0.7)  
    : Math.round(containerPx * 0.6); 
  
  const offset = count === 2 
    ? Math.round(containerPx * 0.35)
    : Math.round(containerPx * 0.22);

  return (
    <div 
      className={cn("relative", className)}
      style={{ width: containerPx, height: containerPx }}
      data-testid="composite-favicon"
    >
      {displayUrls.map((url, index) => (
        <img
          key={index}
          src={url}
          alt={`Station logo ${index + 1}`}
          className="absolute rounded object-cover border border-white shadow-sm"
          style={{
            width: iconSizePx,
            height: iconSizePx,
            top: index * offset,
            left: index * offset,
            zIndex: count - index,
          }}
          onError={() => handleImageError(url)}
        />
      ))}
      {workingUrls.length > 3 && (
        <div 
          className="absolute rounded bg-gray-200 border border-white shadow-sm flex items-center justify-center text-xs font-medium text-gray-600"
          style={{
            width: iconSizePx,
            height: iconSizePx,
            top: 3 * offset,
            left: 3 * offset,
            zIndex: 0,
          }}
        >
          +{workingUrls.length - 3}
        </div>
      )}
    </div>
  );
}
